import React from 'react';
import { FilterOption, getOperator, isNullOperator, OpenSelect, StringConditionType, TextInput } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, SxProps } from '@mui/material';
import { isJSON } from '@utils/utils';
import TokenFilterInput from './TokenFilterInput/TokenFilterInput';
import { TokenSuggestion } from './TokenFilterInput/types';

const FilterInput = ({
  numberField,
  onChange,
  operator,
  value,
  options,
  size = 'small',
  placeholder = 'Type Value',
  width,
  inputWidth,
  disabled = isNullOperator(operator),
  error,
  optionsUseQueryProps,
  skeletonLoading = false,
  endAdornment,
  readOnly,
  sx,
  autoFocus,
  tokenSuggestions,
  onTokenSelect,
}: {
  numberField: boolean;
  operator: string;
  value: any;
  onChange: (value) => void;
  size?: 'small' | 'medium' | 'xSmall';
  placeholder?: string;
  width?: number;
  inputWidth?: number;
  options: FilterOption[];
  disabled?: boolean;
  error?: any;
  optionsUseQueryProps?: { options: any[]; isLoading: boolean };
  skeletonLoading?: boolean;
  endAdornment?: React.ReactNode;
  readOnly?: boolean;
  tokenSuggestions?: TokenSuggestion[];
  onTokenSelect?: (token: TokenSuggestion) => void;
  sx?: SxProps;
  autoFocus?: boolean;
}) => {
  const inputOptions = operator && options.find(({ value }) => value === getOperator(operator))?.inputOptions;

  if (inputOptions) {
    return (
      <Box sx={{ ...flex.itemsCenter, gap: 1, width }}>
        <TextInput
          sx={{ width: inputWidth, ...inputOptions.sx1 }}
          size={size}
          placeholder={inputOptions.placeholder1 || placeholder}
          value={defaultToEmpty(() => inputOptions.getValue1(value))}
          disableClearButton
          type={numberField ? 'number' : 'text'}
          onChange={newVal => onChange(inputOptions.onChange1(newVal, value))}
          error={error && inputOptions.getValue1(error)}
          inputProps={{ endAdornment, readOnly }}
        />
        {inputOptions.separator}
        <TextInput
          sx={{ width: inputWidth, ...inputOptions.sx2 }}
          size={size}
          placeholder={inputOptions.placeholder2 || placeholder}
          value={defaultToEmpty(() => inputOptions.getValue2(value))}
          disableClearButton
          onChange={newVal => onChange(inputOptions.onChange2(newVal, value))}
          error={error && inputOptions.getValue2(error)}
          inputProps={{ endAdornment, readOnly }}
          type={numberField ? 'number' : 'text'}
        />
      </Box>
    );
  }

  if (optionsUseQueryProps && [StringConditionType.EQUALS, StringConditionType.NOT_EQUAL].includes(operator as StringConditionType)) {
    return (
      <OpenSelect
        style={{ width: inputWidth }}
        size={size}
        options={optionsUseQueryProps.options}
        loading={optionsUseQueryProps.isLoading}
        skeletonLoading={skeletonLoading}
        value={value || null}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        disableClearButton
        isRequired
        optionsUseQueryProps={optionsUseQueryProps}
        readOnly={readOnly}
      />
    );
  }

  if (tokenSuggestions?.length) {
    return (
      <TokenFilterInput
        value={value}
        onChange={onChange}
        onTokenSelect={onTokenSelect}
        tokenSuggestions={tokenSuggestions}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        error={error}
        inputWidth={inputWidth}
      />
    );
  }

  return (
    <TextInput
      sx={{ width: inputWidth, ...sx }}
      autoFocus={autoFocus}
      size={size}
      placeholder={placeholder}
      value={isJSON(value) ? '' : value}
      type={numberField ? 'number' : 'text'}
      disableClearButton={numberField}
      disabled={disabled}
      onChange={onChange}
      error={error}
      inputProps={{ endAdornment, readOnly }}
    />
  );
};

export default FilterInput;

function defaultToEmpty(getValue) {
  try {
    return getValue() || '';
  } catch {
    return '';
  }
}
